import { axiosClientAdmin } from '@/lib/auth';
import { QueryClient, useMutation, useQuery } from '@tanstack/react-query';
import {
  SubCameraDto,
  NVRDto,
  IntegrationScriptLogDto,
  IntegrationLimitedDto,
} from '@/lib/integrationTypes/integrationTypes';
import { FieldValues } from 'react-hook-form';
import { AxiosError } from 'axios';
import { CustomerDto } from './customerHooks';
import {
  CreateDevice,
  CreateDeviceResponse,
  DevicesFilterSet,
  DeviceSnapshotResponse,
  NvrFullDto,
} from '@/lib/integrationTypes/ihub-devices';
import { FetchSubDevicesQuery } from '@/lib/api/devices/devices';
import { AddressDto } from '@/lib/api/types.gen';

export interface CreateExternalConnectionDevice {
  name: string;
  description: string;
  serialNumber: string;
  externalConnectionId: string;
  deviceCreationChoices: FieldValues;
  customerId: string;
  projectId: string;
  address: AddressDto;
  id: string | null;
}

export type SaveDeviceForm = {
  nvr: Omit<NVRDto, 'id'>;
  connectionSettings: FieldValues;
  monitoredDevices: {
    id: string;
    monitored: boolean;
    quillNotes?: string;
    description?: string;
  }[];
  integrationId: string;
  customerId: string;
  externalConnectionChoice?: FieldValues;
};

export type CameraFullDto = { monitored: boolean } & SubCameraDto;

export type SubDeviceResponse = {
  secondaryDevices: CameraFullDto[];
};

/** @deprecated */
export const useCreateDeviceMutation = () =>
  useMutation({
    mutationFn: async (newDevice: CreateDevice) => {
      const res = await axiosClientAdmin.post<CreateDeviceResponse>(
        '/Integration/Device/connect',
        newDevice
      );
      return res.data;
    },
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

/** @deprecated */
export const useCreateExternalConnectionDeviceMutation = () =>
  useMutation({
    mutationFn: async (newDevice: CreateExternalConnectionDevice) => {
      const res = await axiosClientAdmin.post<CreateDeviceResponse>(
        '/Integration/ExternalConnection/create-device',
        newDevice
      );

      return res.data;
    },
    onSuccess: () => {
      // queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

export const useSnapshotDeviceQuery = ({ id, enabled }: { id: string; enabled: boolean }) =>
  useQuery({
    queryKey: ['device_snapshot', id],
    queryFn: async () => {
      const res = await axiosClientAdmin.get<DeviceSnapshotResponse>(
        `/Integration/Device/${id}/snapshot`
      );
      return res.data?.b64Img;
    },
    enabled,
  });

type UseSearchDevices = {
  searchQuery: DevicesFilterSet;
};

export type DeviceDto = {
  nvr: NVRDto;
  customer: CustomerDto;
  integration: IntegrationLimitedDto;
};

export type DevicesSearched = {
  devices: DeviceDto[];
};

export const useSearchDevices = ({ searchQuery }: UseSearchDevices) =>
  useQuery({
    queryKey: ['devices', 'search', searchQuery],
    queryFn: async () => {
      const filterSet = new URLSearchParams();

      filterSet.set('PageNumber', '1');
      filterSet.set('PageSize', '5000');
      filterSet.set('SortBy', 'name');
      filterSet.set('SortDir', 'Ascending');

      filterSet.set('Filter.Operator', 'And');

      filterSet.set('Filter.FilterGroups[0][operator]', 'Or');
      filterSet.set('Filter.FilterGroups[0][filters][0][field]', 'name');
      filterSet.set('Filter.FilterGroups[0][filters][0][action]', 'ILike');
      filterSet.set('Filter.FilterGroups[0][filters][0][value]', searchQuery.query);

      filterSet.set('Filter.FilterGroups[0][filters][1][field]', 'serialNumber');
      filterSet.set('Filter.FilterGroups[0][filters][1][action]', 'ILike');
      filterSet.set('Filter.FilterGroups[0][filters][1][value]', searchQuery.query);

      filterSet.set('Filter.FilterGroups[0][filters][2][field]', 'id');
      filterSet.set('Filter.FilterGroups[0][filters][2][action]', 'ILike');
      filterSet.set('Filter.FilterGroups[0][filters][2][value]', searchQuery.query);

      return await axiosClientAdmin
        .get('/Integration/Device/search?' + filterSet.toString())
        .then((response) => response.data as DevicesSearched);
    },
  });

type UseDeviceOptions = {
  id: string;
  /** Optional flag to enable/disable the query */
  enabled?: boolean;
};

/** @deprecated */
export const useDevice = ({ id, enabled = true }: UseDeviceOptions) =>
  useQuery({
    queryKey: ['devices', id],
    queryFn: () =>
      axiosClientAdmin
        .get(`/Integration/Device/${id}`)
        .then(({ data }) => data.device as NvrFullDto),
    retry: (_count, error) => error instanceof AxiosError && error.status !== 404,
    enabled: enabled && !!id,
  });

export const useSubdevices = ({ id }: UseDeviceOptions) =>
  useQuery({
    // TODO: Properly refactor at some point
    queryKey: FetchSubDevicesQuery({ deviceId: id }).queryKey,
    queryFn: () =>
      axiosClientAdmin
        .get<SubDeviceResponse>(`/Integration/Device/${id}/secondary`)
        .then(({ data }) => data.secondaryDevices),
    experimental_prefetchInRender: true,
  });

type UseSaveDeviceMutationOptions = {
  id: string;
  queryClient: QueryClient;
};

/** @deprecated */
export const useSaveDeviceMutation = ({ id, queryClient }: UseSaveDeviceMutationOptions) =>
  useMutation({
    mutationKey: ['devices', id],
    mutationFn: (form: SaveDeviceForm) =>
      axiosClientAdmin.post(`/Integration/Device/${id}/save`, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices', id] });
    },
  });

interface SecondaryDevice {
  id: string;
  name: string;
  monitored: boolean;
  quillNotes?: string;
  description?: string;
}

/** @deprecated */
export const useUpdateSecondaryDevicesMutation = () =>
  useMutation({
    mutationFn: async ({
      primaryDeviceId,
      devices,
    }: {
      primaryDeviceId: string;
      devices: SecondaryDevice[];
    }) => {
      const res = await axiosClientAdmin.post(`/Integration/Device/${primaryDeviceId}/secondary`, {
        devices,
        primaryDeviceId,
      });
      return res.data;
    },
  });

export const useDeleteDeviceMutation = ({
  id,
  queryClient,
}: {
  id: string;
  queryClient: QueryClient;
}) =>
  useMutation({
    mutationFn: async (form: SaveDeviceForm) => {
      await axiosClientAdmin.post(`/Integration/Device/${id}/delete`, form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });
